import React, { useState } from "react";
import { FaEdit, FaTrash, FaMapMarkerAlt, FaClock, FaImage, FaUsers } from "react-icons/fa";
import LoadingSpinner from "../../components/LoadingSpinner";
import ImageEditModal from "./ImageEditModal";

export const LocationCard = ({
  location,
  onEdit,
  onUpdate,
  onManageStaff,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleCloseModal = () => setModalOpen(false);

  const handleSaveImages = (updatedLocationData) => {
    if (onUpdate) {
      onUpdate(updatedLocationData);
    }
    handleCloseModal();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (timestamp instanceof Date) {
      return timestamp.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return String(timestamp);
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-700 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-700 border-red-200";
      case "pending":
      default:
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
    }
  };

  const InfoRow = ({ label, value, className = "" }) => (
    <div className={`flex flex-col ${className}`}>
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </span>
      <span className="text-gray-800 text-sm break-words leading-tight">
        {value || "—"}
      </span>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-7 flex flex-col hover:shadow-xl transition-all duration-300 h-full">
      {/* Header with Actions */}
      <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-xl shadow-md flex-shrink-0">
            <FaMapMarkerAlt />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-xl leading-tight truncate pr-2">
              {location.name}
            </h3>
            <span className="inline-block mt-1 px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-semibold">
              ID: {location.locationId}
            </span>
          </div>
        </div>
        <div className="flex space-x-2 flex-shrink-0">
          {/* 🔹 Staff Management: Only for Approved stores */}
          {location.status === "approved" && (
            <button
              onClick={() => onManageStaff && onManageStaff(location.id)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg transition-colors font-medium text-sm"
              title="Manage Staff"
            >
              <FaUsers className="w-4 h-4" />
              <span className="hidden sm:inline">Staff</span>
            </button>
          )}

          {/* 🔹 Edit: Hidden while Pending */}
          {location.status !== "pending" && (
            <button
              onClick={() => onEdit && onEdit(location.id)}
              className="p-2 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors duration-200"
              title="Edit Details"
            >
              <FaEdit className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Main Info Sections */}
      <div className="flex-grow space-y-6">
        
        {/* Basic Location Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <InfoRow
            label="Address"
            value={`${location.address}, ${location.city}, ${location.state} ${location.zip}`}
            className="col-span-full"
          />
          <InfoRow label="Contact" value={location.contact} />
          
          {/* New Operating Hours Section */}
          <div className="flex flex-col">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Hours
            </span>
            <div className="flex items-center text-gray-800 text-sm">
              <FaClock className="mr-1.5 text-blue-500 text-xs" />
              <span>
                {location.openTime ? formatTime(location.openTime) : "--"} -{" "}
                {location.closeTime ? formatTime(location.closeTime) : "--"}
              </span>
            </div>
          </div>
        </div>

        {/* Legal & Business Details */}
        <div className="border-t border-gray-100 pt-5">
          <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase">
            Legal Entity Info
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <InfoRow label="Legal Name" value={location.businessName} />
            <InfoRow label="Tax ID / EIN" value={location.businessIdNumber} />
            <InfoRow label="Owner Name" value={location.ownerName} />
            <InfoRow label="Owner Email" value={location.ownerEmail} />
          </div>
        </div>

        {/* Documents Preview (Count only) */}
        <div className="border-t border-gray-100 pt-5">
          <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase">
            Verification Docs
          </h4>
          <div className="flex gap-2">
            {location.verificationDocuments?.map((doc, idx) => (
               <a 
                 key={idx} 
                 href={doc.url} 
                 target="_blank" 
                 rel="noreferrer"
                 className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded border hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
               >
                 {doc.type === "EIN_Certificate" ? "EIN" : doc.type === "Business_License" ? "License" : "Proof"}
               </a>
            ))}
          </div>
        </div>

        {/* Status Section */}
        <div className="border-t border-gray-100 pt-5">
          <div className="flex justify-between items-start">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                Status
              </span>
              <span
                className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase rounded-full border ${getStatusClasses(
                  location.status
                )}`}
              >
                {location.status || "pending"}
              </span>
            </div>
            {location.verifiedAt && (
               <InfoRow 
                 label="Verified On" 
                 value={formatTimestamp(location.verifiedAt)} 
                 className="items-end text-right"
               />
            )}
          </div>
          {location.adminRemarks && (
            <div className="mt-3 bg-red-50 p-3 rounded-lg border border-red-100">
              <span className="text-xs font-bold text-red-800 uppercase block mb-1">
                Admin Remarks:
              </span>
              <p className="text-sm text-red-700">{location.adminRemarks}</p>
            </div>
          )}
        </div>
      </div>

      {/* Store Images */}
      {location.storeImages?.length > 0 && (
        <div className="mt-7 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-600 uppercase mb-3 flex items-center justify-between">
            <span>Store Photos ({location.storeImages.length})</span>
          </h4>
          <div className="flex flex-wrap gap-3">
            {location.storeImages.slice(0, 4).map((img, i) => (
              <a
                key={i}
                href={img.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-20 h-20 rounded-lg overflow-hidden shadow-sm border border-gray-200 hover:border-blue-500 transition-all duration-200"
              >
                <img
                  src={img.url}
                  alt={`Store ${i}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              </a>
            ))}
            {location.storeImages.length > 4 && (
               <div className="w-20 h-20 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 text-xs font-medium">
                  +{location.storeImages.length - 4} more
               </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {modalOpen && (
        <ImageEditModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          location={location}
          onSave={handleSaveImages}
        />
      )}
    </div>
  );
};

// Helper to convert "14:30" to "2:30 PM"
const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hour, minute] = timeStr.split(":");
  const h = parseInt(hour, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const formattedHour = h % 12 || 12;
  return `${formattedHour}:${minute} ${suffix}`;
};